// components/profile/hooks/useProfileForm.js
import { useState, useCallback, useEffect } from 'react';
import { useProfileFormContext } from '../context/ProfileFormContext';
import { updateJewellerProfile } from '../../../services/jeweller';
import { useAuth } from '../../../contexts/AuthContext';
import { REQUIRED_FIELDS } from '../utils/validation';

const normalizeExternalUrl = (value) => {
  const trimmed = (value || '').trim();

  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

export const useProfileForm = () => {
  const {
    formData,
    errors,
    touched,
    isDirty,
    isSubmitting,
    isActive, // NEW: Get isActive from context
    validateForm,
    setIsSubmitting,
    setFieldErrors,
    resetForm,
    getCompletionPercentage,
    getChangedFields
  } = useProfileFormContext();

  const { jeweller, refreshJewellerData } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    setSuccess(false);
    setSaveError(null);

    // Validate the form and get all errors (respects isActive)
    const validationErrors = validateForm(true);
    const hasErrors = Object.keys(validationErrors).length > 0;

    if (hasErrors) {
      const firstErrorField = Object.keys(validationErrors)[0];
      const fieldLabel = REQUIRED_FIELDS[firstErrorField]?.replace(' is required', '') || firstErrorField;

      // Show appropriate error message based on active status
      if (isActive) {
        setSaveError(`Please review the '${fieldLabel}' field before saving.`);
      } else {
        // For non-active jewellers, only format errors should appear
        setSaveError(`Please review the '${fieldLabel}' field.`);
      }
      
      const element = document.getElementById(`form-field-${firstErrorField}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return false;
    }

    if (!jeweller?.id) {
      setSaveError('Unable to identify jeweller account. Please refresh and try again.');
      return false;
    }

    setLoading(true);
    setIsSubmitting(true);

    try {
      const changedFields = getChangedFields();
      const normalizedVideoUrl = formData.showcaseVideo || formData.videoUrl || '';
      const normalizedGalleryPhotos = Array.isArray(formData.galleryImages)
        ? formData.galleryImages
        : Array.isArray(formData.galleryPhotos)
          ? formData.galleryPhotos
          : formData.galleryPhotos && typeof formData.galleryPhotos === 'object'
            ? Object.values(formData.galleryPhotos)
            : [];

      // SIMPLE FIX: Derive ethicalJeweller from ethicalStatement
      const hasEthicalStatement = formData.ethicalStatement && 
                                  formData.ethicalStatement.trim().length >= 100;
      const normalizedWebsite = normalizeExternalUrl(formData.website);
      const normalizedInstagram = normalizeExternalUrl(formData.instagram);
      
      const dataToSubmit = {
        ...changedFields,
        updatedAt: new Date().toISOString(),
        completionPercentage: getCompletionPercentage()
      };

      if (
        'showcaseVideo' in changedFields ||
        'videoUrl' in changedFields
      ) {
        dataToSubmit.videoUrl = normalizedVideoUrl;
        dataToSubmit.showcaseVideo = normalizedVideoUrl;
      }

      if (
        'galleryImages' in changedFields ||
        'galleryPhotos' in changedFields
      ) {
        dataToSubmit.galleryPhotos = normalizedGalleryPhotos;
        dataToSubmit.galleryImages = normalizedGalleryPhotos;
      }

      if (
        'ethicalStatement' in changedFields ||
        'tags' in changedFields
      ) {
        dataToSubmit.tags = {
          ...formData.tags,
          ethicalJeweller: hasEthicalStatement
        };
        dataToSubmit.ethicalStatement = hasEthicalStatement ? formData.ethicalStatement : '';
      }

      if (dataToSubmit.tags) {
        console.log('Submitting with derived ethicalJeweller:', dataToSubmit.tags.ethicalJeweller);
        console.log('Based on statement:', dataToSubmit.ethicalStatement);
      }

      if ('website' in changedFields || formData.website !== normalizedWebsite) {
        dataToSubmit.website = normalizedWebsite;
      }

      if ('instagram' in changedFields || formData.instagram !== normalizedInstagram) {
        dataToSubmit.instagram = normalizedInstagram;
      }

      await updateJewellerProfile(jeweller.id, dataToSubmit);
      await refreshJewellerData();
      
      // Reset with the submitted data to update the initial reference
      resetForm({
        ...formData,
        website: normalizedWebsite,
        instagram: normalizedInstagram,
        videoUrl: normalizedVideoUrl,
        showcaseVideo: normalizedVideoUrl,
        galleryPhotos: normalizedGalleryPhotos,
        galleryImages: normalizedGalleryPhotos,
        tags: {
          ...formData.tags,
          ethicalJeweller: hasEthicalStatement
        },
        ethicalStatement: hasEthicalStatement ? formData.ethicalStatement : ''
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
      
      return true;
    } catch (error) {
      console.error('Profile update error:', error);
      setSaveError(error.message || 'Failed to update profile. Please try again.');
      return false;
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  }, [
    formData, 
    validateForm, 
    jeweller, 
    refreshJewellerData, 
    setIsSubmitting, 
    getCompletionPercentage,
    getChangedFields,
    resetForm,
    isActive // NEW: Include isActive in dependencies
  ]);

  // Handle section save (for future use with section-by-section saving)
  const handleSectionSave = useCallback(async (sectionData) => {
    if (!jeweller?.id) {
      setSaveError('Unable to identify jeweller account.');
      return false;
    }

    try {
      await updateJewellerProfile(jeweller.id, {
        ...sectionData,
        updatedAt: new Date().toISOString()
      });
      
      await refreshJewellerData();
      return true;
    } catch (error) {
      console.error('Section save error:', error);
      setSaveError('Failed to save section. Please try again.');
      return false;
    }
  }, [jeweller, refreshJewellerData]);

  // Check for unsaved changes before page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isSubmitting]);

  // Reset success message when form becomes dirty again
  useEffect(() => {
    if (isDirty && success) {
      setSuccess(false);
    }
  }, [isDirty, success]);

  return {
    // Form data and state
    formData,
    errors,
    touched,
    isDirty,
    isActive, // NEW: Expose isActive
    
    // Submission state
    loading,
    success,
    saveError,
    
    // Actions
    handleSubmit,
    handleSectionSave,
    resetForm,
    
    // Helpers
    completionPercentage: getCompletionPercentage()
  };
};
